import { ChangeEvent, useContext, useRef, useState } from 'react';
import { styles } from './UploadFile.style';
import { Stack, Typography, Box } from '@mui/material';
import { LocalizationContext } from 'src/Context/LocalizationContext';
import { FileUploadIcon } from 'src/icons/fileUpload/FileUpload';
import { DragDropFile } from './DragAndDropFile';

interface IUploadFileProps {
	uploadAttachment: (data: File[]) => void;
	containerRef: any;
	activateContainerDropArea: (isDragActive: boolean) => void;
}

export default function UploadFileComponent(props: IUploadFileProps) {
	const { FileUpload: text, A11Y } = useContext(LocalizationContext);
	const [onFileDragActive, setOnFileDragActive] = useState<boolean>(false);
	const fileInputRef = useRef<HTMLInputElement>(null); // Ref for file picker

	// Open the system's file picker
	const openFilePicker = (event: any) => {
		if (event.type === 'keydown') {
			if (event?.code !== 'Enter' && event?.code !== 'Space') return;
		}
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	// Handle file select with file picker
	const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files) {
			props.uploadAttachment(Array.from(files));
		}
	};

	// Handle file select with drag and drop
	const uploadAttachment = (files: File[]) => {
		props.uploadAttachment(files);
	};

	return (
		<div data-cy="pqAddSupportingDocumentsComponent">
			<Box
				tabIndex={0}
				sx={styles.container}
				aria-label={A11Y.ClickToUploadDocuments}
				onKeyDown={openFilePicker}
				onClick={openFilePicker}>
				<Stack
					justifyContent={{
						xs: 'flex-start',
						md: 'center',
					}}
					alignItems={'center'}
					direction="row"
					sx={styles.items}
					gap={'20px'}
					marginY={{ xs: 0, md: '5%' }}>
					{/* Icon */}
					<FileUploadIcon style={onFileDragActive ? { opacity: '0' } : {}} />
					<Stack direction="column" textAlign={'start'} sx={onFileDragActive ? { opacity: '0' } : {}}>
						{/* Header */}
						<Typography variant={'supportingDocumentsTitle'}>{text.UploadButtonHeader}</Typography>
						{/* Subheader */}
						<Typography variant={'supportingDocumentsSubtitle'}>{text.UploadButtonSubheader}</Typography>
					</Stack>
				</Stack>
				{/* Drag and Drop functionality */}
				<DragDropFile
					parentRef={props.containerRef}
					uploadFiles={uploadAttachment}
					setOnFileDrag={(onFileDrag) => setOnFileDragActive(onFileDrag)}
					activateParentDropArea={props.activateContainerDropArea}
				/>
				{/* Invisible input for file picker functionality. Filters files by supported types */}
				{!onFileDragActive && (
					<input
						data-cy="pqUploadAttachmentMangerInput"
						type="file"
						ref={fileInputRef}
						style={{ display: 'none' }}
						onChange={handleFileSelect}
						multiple
						// Turning accept filter on breaks the drag and drop functionality. Accept only works on Mac machines (PCs ignore it)
						// accept={`.${Attachments.SupportedExtensions.join(', .')}`}
					/>
				)}
			</Box>
		</div>
	);
}
